export const connectorIds = [
  "firebase",
  "cloudflare",
  "clover",
  "email",
  "sms",
  "storage",
  "calendar",
  "analytics",
  "clinical",
  "accounting",
] as const;

export type ConnectorId = (typeof connectorIds)[number];

export const connectorEnvironments = [
  "development",
  "staging",
  "production",
] as const;

export type ConnectorEnvironment = (typeof connectorEnvironments)[number];

export const connectorStatuses = [
  "DISABLED",
  "NEEDS_CONFIGURATION",
  "ACTION_REQUIRED",
  "TESTING",
  "SIMULATED",
  "HEALTHY",
  "DEGRADED",
  "FAILED",
  "BLOCKED",
  "REVOKED",
] as const;

export type ConnectorStatus = (typeof connectorStatuses)[number];

/**
 * Configuration lifecycle is operator-authored. HEALTHY, DEGRADED, FAILED,
 * and TESTING are deliberately absent because only diagnostic evidence may
 * derive those connection states.
 */
export const connectorConfigurationLifecycles = [
  "DISABLED",
  "NEEDS_CONFIGURATION",
  "ACTION_REQUIRED",
  "SIMULATED",
  "BLOCKED",
  "REVOKED",
  "ENABLED",
] as const;

export type ConnectorConfigurationLifecycle =
  (typeof connectorConfigurationLifecycles)[number];

export const connectorSecretReferenceStatuses = [
  "MISSING",
  "REFERENCED",
  "VERIFIED",
  "REVOKED",
] as const;

export type ConnectorSecretReferenceStatus =
  (typeof connectorSecretReferenceStatuses)[number];

export const connectorDiagnosticOutcomes = [
  "RUNNING",
  "PASSED",
  "DEGRADED",
  "FAILED",
  "BLOCKED",
] as const;

export type ConnectorDiagnosticOutcome =
  (typeof connectorDiagnosticOutcomes)[number];

export const connectorDiagnosticCheckStatuses = [
  "PASSED",
  "FAILED",
  "SKIPPED",
  "BLOCKED",
] as const;

export type ConnectorDiagnosticCheckStatus =
  (typeof connectorDiagnosticCheckStatuses)[number];

export const connectorDiagnosticCheckCodes = [
  "CONFIG_PRESENT",
  "SECRET_REFERENCES_VERIFIED",
  "AUTHORIZATION_VALID",
  "CALLBACK_REACHABLE",
  "WEBHOOK_VERIFIED",
  "POLICY_APPROVED",
  "BOUNDARY_APPROVED",
  "DEPENDENCIES_HEALTHY",
] as const;

export type ConnectorDiagnosticCheckCode =
  (typeof connectorDiagnosticCheckCodes)[number];

export const connectorSafeErrorCodes = [
  "CONFIG_MISSING",
  "AUTHORIZATION_RENEWAL_REQUIRED",
  "DEPENDENCY_NOT_READY",
  "PROVIDER_UNAVAILABLE",
  "CALLBACK_UNREACHABLE",
  "POLICY_APPROVAL_REQUIRED",
  "COMPLIANCE_BOUNDARY_BLOCKED",
  "TEST_FAILED",
] as const;

export type ConnectorSafeErrorCode = (typeof connectorSafeErrorCodes)[number];

export const connectorEventTypes = [
  "CONFIGURATION_VERSION_CREATED",
  "OWNER_ASSIGNED",
  "TEST_RECORDED",
  "ENABLED",
  "DISABLED",
  "TOKEN_ROTATION_REQUESTED",
  "TOKEN_ROTATION_RECORDED",
  "REVOKED",
  "OVERRIDE_BLOCKED",
] as const;

export type ConnectorEventType = (typeof connectorEventTypes)[number];

export interface ConnectorSecretReference {
  readonly name: string;
  readonly status: ConnectorSecretReferenceStatus;
  readonly lastValidatedAt: string | null;
}

export interface ConnectorDiagnosticCheck {
  readonly code: ConnectorDiagnosticCheckCode;
  readonly status: ConnectorDiagnosticCheckStatus;
}

export interface ConnectorDiagnostic {
  readonly testId: string;
  readonly outcome: ConnectorDiagnosticOutcome;
  readonly startedAt: string;
  readonly completedAt: string | null;
  readonly checks: readonly ConnectorDiagnosticCheck[];
  readonly correlationId: string;
}

export interface ConnectorSafeError {
  readonly code: ConnectorSafeErrorCode;
}

export interface ConnectorSummary {
  readonly connectorId: ConnectorId;
  readonly category: string;
  readonly name: string;
  readonly description: string;
  readonly environment: ConnectorEnvironment;
  readonly provider: string;
  readonly mode: string;
  readonly owner: string | null;
  readonly operationalContact: string | null;
  readonly configurationVersion: number | null;
  readonly safeIdentifierAlias: string | null;
  readonly callbackUrl: string | null;
  readonly lifecycle: ConnectorConfigurationLifecycle;
  readonly enabled: boolean;
  readonly status: ConnectorStatus;
  readonly capabilities: readonly string[];
  readonly requiredSecrets: readonly ConnectorSecretReference[];
  readonly dependencies: readonly ConnectorId[];
  readonly leastPrivilegeScope: string;
  readonly lastDiagnostic: ConnectorDiagnostic | null;
  readonly lastError: ConnectorSafeError | null;
}

export interface ConnectorEvent {
  readonly eventId: string;
  readonly type: ConnectorEventType;
  readonly configurationVersion: number | null;
  readonly actorUserId: string | null;
  readonly correlationId: string;
  readonly occurredAt: string;
}

export interface ConnectorRegistrySnapshot {
  readonly scope: "ORGANIZATION";
  readonly organizationId: string;
  readonly environment: ConnectorEnvironment;
  readonly businessUnitOverridesAllowed: false;
  readonly generatedAt: string;
  readonly connectors: readonly ConnectorSummary[];
}

export interface ConnectorDetailSnapshot {
  readonly scope: "ORGANIZATION";
  readonly organizationId: string;
  readonly environment: ConnectorEnvironment;
  readonly businessUnitOverridesAllowed: false;
  readonly generatedAt: string;
  readonly connector: ConnectorSummary;
  readonly recentEvents: readonly ConnectorEvent[];
}

const connectorIdSet: ReadonlySet<string> = new Set(connectorIds);
const connectorEnvironmentSet: ReadonlySet<string> = new Set(
  connectorEnvironments,
);
const connectorStatusSet: ReadonlySet<string> = new Set(connectorStatuses);
const connectorLifecycleSet: ReadonlySet<string> = new Set(
  connectorConfigurationLifecycles,
);
const secretReferenceStatusSet: ReadonlySet<string> = new Set(
  connectorSecretReferenceStatuses,
);
const diagnosticOutcomeSet: ReadonlySet<string> = new Set(
  connectorDiagnosticOutcomes,
);
const diagnosticCheckStatusSet: ReadonlySet<string> = new Set(
  connectorDiagnosticCheckStatuses,
);
const diagnosticCheckCodeSet: ReadonlySet<string> = new Set(
  connectorDiagnosticCheckCodes,
);
const safeErrorCodeSet: ReadonlySet<string> = new Set(connectorSafeErrorCodes);
const connectorEventTypeSet: ReadonlySet<string> = new Set(connectorEventTypes);

const safeIdentifierPattern = /^[A-Za-z0-9_-]{3,128}$/;
const safeCodePattern = /^[A-Z][A-Z0-9_]{1,127}$/;
const safeSecretNamePattern = /^[A-Z][A-Z0-9_]{2,127}$/;
const isoInstantPattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;

const connectorSummaryKeys = [
  "connectorId",
  "category",
  "name",
  "description",
  "environment",
  "provider",
  "mode",
  "owner",
  "operationalContact",
  "configurationVersion",
  "safeIdentifierAlias",
  "callbackUrl",
  "lifecycle",
  "enabled",
  "status",
  "capabilities",
  "requiredSecrets",
  "dependencies",
  "leastPrivilegeScope",
  "lastDiagnostic",
  "lastError",
] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function hasExactKeys(
  value: Record<string, unknown>,
  keys: readonly string[],
): boolean {
  const actual = Object.keys(value);
  return actual.length === keys.length && keys.every((key) => key in value);
}

function isSafeText(value: unknown, maximumLength: number): value is string {
  return (
    typeof value === "string" &&
    value.length > 0 &&
    value.length <= maximumLength &&
    !/[\u0000-\u001f\u007f]/.test(value)
  );
}

function isNullableSafeText(
  value: unknown,
  maximumLength: number,
): value is string | null {
  return value === null || isSafeText(value, maximumLength);
}

function isSafeHttpsUrl(value: unknown): value is string {
  if (
    typeof value !== "string" ||
    value.length < 9 ||
    value.length > 1024 ||
    /[\u0000-\u0020\u007f]/.test(value)
  ) {
    return false;
  }
  try {
    const url = new URL(value);
    return (
      url.protocol === "https:" &&
      url.username === "" &&
      url.password === "" &&
      url.search === "" &&
      url.hash === "" &&
      url.hostname.length > 0 &&
      url.toString() === value
    );
  } catch {
    return false;
  }
}

function isIsoInstant(value: unknown): value is string {
  return (
    typeof value === "string" &&
    isoInstantPattern.test(value) &&
    new Date(value).toISOString() === value
  );
}

function isNullableIsoInstant(value: unknown): value is string | null {
  return value === null || isIsoInstant(value);
}

function isUniqueStrings(values: readonly string[]): boolean {
  return new Set(values).size === values.length;
}

export function isConnectorId(value: unknown): value is ConnectorId {
  return typeof value === "string" && connectorIdSet.has(value);
}

export function isConnectorEnvironment(
  value: unknown,
): value is ConnectorEnvironment {
  return typeof value === "string" && connectorEnvironmentSet.has(value);
}

export function isConnectorStatus(value: unknown): value is ConnectorStatus {
  return typeof value === "string" && connectorStatusSet.has(value);
}

function isConnectorLifecycle(
  value: unknown,
): value is ConnectorConfigurationLifecycle {
  return typeof value === "string" && connectorLifecycleSet.has(value);
}

function isConnectorSecretReference(
  value: unknown,
): value is ConnectorSecretReference {
  if (!isRecord(value)) return false;
  return (
    hasExactKeys(value, ["name", "status", "lastValidatedAt"]) &&
    typeof value.name === "string" &&
    safeSecretNamePattern.test(value.name) &&
    typeof value.status === "string" &&
    secretReferenceStatusSet.has(value.status) &&
    isNullableIsoInstant(value.lastValidatedAt)
  );
}

function isConnectorDiagnosticCheck(
  value: unknown,
): value is ConnectorDiagnosticCheck {
  if (!isRecord(value)) return false;
  return (
    hasExactKeys(value, ["code", "status"]) &&
    typeof value.code === "string" &&
    diagnosticCheckCodeSet.has(value.code) &&
    typeof value.status === "string" &&
    diagnosticCheckStatusSet.has(value.status)
  );
}

function isConnectorDiagnostic(value: unknown): value is ConnectorDiagnostic {
  if (!isRecord(value)) return false;
  if (
    !hasExactKeys(value, [
      "testId",
      "outcome",
      "startedAt",
      "completedAt",
      "checks",
      "correlationId",
    ]) ||
    typeof value.testId !== "string" ||
    !safeIdentifierPattern.test(value.testId) ||
    typeof value.outcome !== "string" ||
    !diagnosticOutcomeSet.has(value.outcome) ||
    !isIsoInstant(value.startedAt) ||
    !isNullableIsoInstant(value.completedAt) ||
    !Array.isArray(value.checks) ||
    value.checks.length > 64 ||
    !value.checks.every(isConnectorDiagnosticCheck) ||
    typeof value.correlationId !== "string" ||
    !safeIdentifierPattern.test(value.correlationId)
  ) {
    return false;
  }
  return value.outcome === "RUNNING"
    ? value.completedAt === null
    : value.completedAt !== null && value.completedAt >= value.startedAt;
}

function isConnectorSafeError(value: unknown): value is ConnectorSafeError {
  if (!isRecord(value)) return false;
  return (
    hasExactKeys(value, ["code"]) &&
    typeof value.code === "string" &&
    safeErrorCodeSet.has(value.code)
  );
}

function hasValidConnectorSummaryFields(
  value: Record<string, unknown>,
): boolean {
  if (
    !isConnectorId(value.connectorId) ||
    !isSafeText(value.category, 64) ||
    !isSafeText(value.name, 128) ||
    !isSafeText(value.description, 512) ||
    !isConnectorEnvironment(value.environment) ||
    !isSafeText(value.provider, 128) ||
    !isSafeText(value.mode, 128) ||
    !isNullableSafeText(value.owner, 128) ||
    !isNullableSafeText(value.operationalContact, 128) ||
    !(
      value.configurationVersion === null ||
      (Number.isSafeInteger(value.configurationVersion) &&
        (value.configurationVersion as number) >= 1)
    ) ||
    !isNullableSafeText(value.safeIdentifierAlias, 256) ||
    !(value.callbackUrl === null || isSafeHttpsUrl(value.callbackUrl)) ||
    !isConnectorLifecycle(value.lifecycle) ||
    typeof value.enabled !== "boolean" ||
    !isConnectorStatus(value.status) ||
    !Array.isArray(value.capabilities) ||
    value.capabilities.length > 32 ||
    !value.capabilities.every((item) => isSafeText(item, 128)) ||
    !isUniqueStrings(value.capabilities) ||
    !Array.isArray(value.requiredSecrets) ||
    value.requiredSecrets.length > 32 ||
    !value.requiredSecrets.every(isConnectorSecretReference) ||
    !isUniqueStrings(
      value.requiredSecrets.map((item) => item.name as string),
    ) ||
    !Array.isArray(value.dependencies) ||
    value.dependencies.length > connectorIds.length - 1 ||
    !value.dependencies.every(isConnectorId) ||
    !isUniqueStrings(value.dependencies) ||
    value.dependencies.includes(value.connectorId as ConnectorId) ||
    !isSafeText(value.leastPrivilegeScope, 512) ||
    !(
      value.lastDiagnostic === null ||
      isConnectorDiagnostic(value.lastDiagnostic)
    ) ||
    !(value.lastError === null || isConnectorSafeError(value.lastError))
  ) {
    return false;
  }

  if (value.enabled !== (value.lifecycle === "ENABLED")) return false;
  if (value.status !== "HEALTHY") return true;

  const diagnostic = value.lastDiagnostic as ConnectorDiagnostic | null;
  return (
    value.lifecycle === "ENABLED" &&
    diagnostic?.outcome === "PASSED" &&
    diagnostic.completedAt !== null &&
    diagnostic.checks.length > 0 &&
    diagnostic.checks.every(({ status }) => status === "PASSED") &&
    (value.requiredSecrets as readonly ConnectorSecretReference[]).every(
      ({ status }) => status === "VERIFIED",
    )
  );
}

export function isConnectorSummary(value: unknown): value is ConnectorSummary {
  return (
    isRecord(value) &&
    hasExactKeys(value, connectorSummaryKeys) &&
    hasValidConnectorSummaryFields(value)
  );
}

function isConnectorEvent(value: unknown): value is ConnectorEvent {
  if (!isRecord(value)) return false;
  return (
    hasExactKeys(value, [
      "eventId",
      "type",
      "configurationVersion",
      "actorUserId",
      "correlationId",
      "occurredAt",
    ]) &&
    typeof value.eventId === "string" &&
    safeIdentifierPattern.test(value.eventId) &&
    typeof value.type === "string" &&
    connectorEventTypeSet.has(value.type) &&
    (value.configurationVersion === null ||
      (Number.isSafeInteger(value.configurationVersion) &&
        (value.configurationVersion as number) >= 1)) &&
    (value.actorUserId === null ||
      (typeof value.actorUserId === "string" &&
        safeIdentifierPattern.test(value.actorUserId))) &&
    typeof value.correlationId === "string" &&
    safeIdentifierPattern.test(value.correlationId) &&
    isIsoInstant(value.occurredAt)
  );
}

function hasValidSnapshotEnvelope(value: Record<string, unknown>): boolean {
  return (
    value.scope === "ORGANIZATION" &&
    typeof value.organizationId === "string" &&
    safeIdentifierPattern.test(value.organizationId) &&
    isConnectorEnvironment(value.environment) &&
    value.businessUnitOverridesAllowed === false &&
    isIsoInstant(value.generatedAt)
  );
}

export function isConnectorRegistrySnapshot(
  value: unknown,
): value is ConnectorRegistrySnapshot {
  if (!isRecord(value)) return false;
  return (
    hasExactKeys(value, [
      "scope",
      "organizationId",
      "environment",
      "businessUnitOverridesAllowed",
      "generatedAt",
      "connectors",
    ]) &&
    hasValidSnapshotEnvelope(value) &&
    Array.isArray(value.connectors) &&
    value.connectors.length === connectorIds.length &&
    value.connectors.every(isConnectorSummary) &&
    value.connectors.every(
      (connector, index) => connector.connectorId === connectorIds[index],
    )
  );
}

export function isConnectorDetailSnapshot(
  value: unknown,
): value is ConnectorDetailSnapshot {
  if (!isRecord(value)) return false;
  return (
    hasExactKeys(value, [
      "scope",
      "organizationId",
      "environment",
      "businessUnitOverridesAllowed",
      "generatedAt",
      "connector",
      "recentEvents",
    ]) &&
    hasValidSnapshotEnvelope(value) &&
    isConnectorSummary(value.connector) &&
    value.connector.environment === value.environment &&
    Array.isArray(value.recentEvents) &&
    value.recentEvents.length <= 100 &&
    value.recentEvents.every(isConnectorEvent)
  );
}
